import React from "react";
import styled from "@emotion/styled";
import { redirectUri, isMac, isRunningInIOSWebview } from "../utils/environment";
import logger from "../utils/logger";

import { useAuth } from "./useAuth";
import { developSignInErrorMessage, isDevelopIdeaflowIdEnabled, useDevelopSession } from "./DevelopSession";

const Button = styled.button`
  unset: all;
`;

const ButtonLink = styled.a`
  color: var(--color-text-primary);
  background-color: none;
  text-decoration: underline;
`;

interface LoginButtonProps {
  className?: string;
  autoFocus?: boolean;
}

const LoginButton: React.FC<LoginButtonProps> = ({ className, autoFocus }) => {
  const { isLoading, user, isAuthenticated, loginWithRedirect, logout, error } = useAuth();

  if (isLoading) {
    return <div>Loading your profile information...</div>;
  }

  if (error) {
    logout();
  }

  // we cannot be authenticated and have no user
  if (isAuthenticated && !user) {
    return (
      <div>
        Oops! We encountered a problem authenticating you. Please refresh by pressing {isMac ? " ⌘ + r" : "ctrl  +r"}
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Button
        className={className}
        autoFocus
        onClick={() => {
          try {
            // We redirect to the default URI when running with the `isRunningInIOSWebview` flag but actually outside of the WkWebView
            // This makes it possible to test the `isRunningInIOSWebview` mode in a single browser tab
            loginWithRedirect(
              isRunningInIOSWebview && window.location.protocol === "thoughtstream-app:"
                ? { redirectUri, login_hint: undefined, prompt: "select_account" }
                : { redirectUri, login_hint: undefined, prompt: "select_account" },
            );
          } catch (e) {
            logger.error(e);
          }
        }}
      >
        {isDevelopIdeaflowIdEnabled() ? "Sign in with Ideaflow" : "Log in"}
      </Button>
    );
  }

  return null;
};

const Note = styled.p`
  margin: 12px 0 0;
  font-size: 14px;
  text-align: center;
  color: var(--color-text-secondary);
`;

/**
 * With Ideaflow ID sign-in on (develop), the login page has one control,
 * "Sign in with Ideaflow". Google, email/password, sign-up and reset happen on
 * id.ideaflow.app; this adds the one helper line and any fixed-copy error from
 * a sign-in that came back without a session.
 */
export function DevelopSignInNotes() {
  const { error } = useDevelopSession();
  const { isAuthenticated, isLoading } = useAuth();
  if (!isDevelopIdeaflowIdEnabled() || isAuthenticated || isLoading) return null;
  return (
    <>
      <Note>New here? You can create an account on the next screen.</Note>
      {error && <Note role="alert">{developSignInErrorMessage(error)}</Note>}
    </>
  );
}

export function PrivacyPolicyButton() {
  return (
    <ButtonLink href="https://www.ideaflow.io/privacy-policy" target="_blank">
      Privacy Policy
    </ButtonLink>
  );
}

export default LoginButton;
